import { useState } from 'react';
import { TablePagination } from '../../components/TablePagination';
import { Badge, Table, Text, Select } from '@mantine/core';
import { IconGift } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { TableToolbar } from '../../components/TableToolbar';
import { TableSkeleton } from '../../components/TableSkeleton';
import { FetchOverlay } from '../../components/FetchOverlay';
import { EmptyState } from '../../components/EmptyState';
import { FeatureFrozen } from '../../components/FeatureFrozen';
import { usePagedQuery } from '../../hooks/usePagedQuery';
import { TableExport } from '../../components/TableExport';
import { formatDate, formatPhone } from '../../lib/format';
import { PREMIUM_UI_ENABLED } from '../../lib/features';
import { useTranslation } from 'react-i18next';

interface Gift {
  id: string; months: number; anonymous: boolean; message?: string | null; createdAt: string; tenantName: string;
  from: { fullName: string; phone?: string | null };
  to: { fullName: string; phone?: string | null };
}
interface TenantOpt { id: string; name: string }

/**
 * Marshrut komponenti (`/saas-gifts`).
 *
 * Premium muzlatilganda (`lib/features.ts` → `PREMIUM_UI_ENABLED = false`) yangi
 * sovg'a umuman yaralmaydi (backend sovg'a endpointi 403), ro'yxat esa faqat eski
 * yozuvlarni ko'rsatib turardi. Shu sababli bo'lim yon menyudan olib tashlangan,
 * URL bilan kirilsa "vaqtincha o'chirilgan" holati chiziladi.
 */
export default function SaasGifts() {
  if (!PREMIUM_UI_ENABLED) return <FeatureFrozen titleKey="saasGiftsPage.title" />;
  return <SaasGiftsTable />;
}

/** Superadmin — barcha maktablar bo'ylab Premium sovg'alar nazorati. */
function SaasGiftsTable() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 300);
  const [tenantId, setTenantId] = useState('');
  const [sort, setSort] = useState('createdAt:desc');

  // Maktab ro'yxati (SaasPremium bilan bir xil kesh kaliti — ikkinchi so'rov ketmaydi)
  const { data: tenants = [] } = useQuery({
    queryKey: ['sa-tenants-opt'],
    queryFn: async () => (await api.get<TenantOpt[]>('/superadmin/tenants')).data,
  });

  /* Filtrlar BITTA joyda: jadval ham, eksport ham AYNI obyektni oladi. */
  const filtrlar = {
    q: q || undefined, tenantId: tenantId || undefined, sort,
  };
  const gifts = usePagedQuery<Gift>('sa-gifts', '/superadmin/gifts', filtrlar);
  // Filtrlangan bo'sh natija ma'lumot umuman yo'qligidan farqli matn ko'rsatadi
  const filterActive = !!q || !!tenantId;

  return (
    <>
      <PageHeader title={t('saasGiftsPage.title')} subtitle={`${t('common.total')}: ${gifts.total}`} />
      <TableToolbar
        search={search} onSearch={(v) => { setSearch(v); gifts.setPage(1); }} searchPlaceholder={t('saasGiftsPage.searchPlaceholder')}
        sort={sort} onSort={setSort}
        sortOptions={[
          { value: 'createdAt:desc', label: t('saasGiftsPage.sortDateDesc') },
          { value: 'createdAt:asc', label: t('saasGiftsPage.sortDateAsc') },
          { value: 'months:desc', label: t('saasGiftsPage.sortMonthsDesc') },
        ]}
      >
        <Select
          placeholder={t('saasGiftsPage.tenant')}
          data={tenants.map((tn) => ({ value: tn.id, label: tn.name }))}
          value={tenantId || null}
          onChange={(v) => setTenantId(v ?? '')}
          clearable searchable w={190}
        />
        <TableExport<Gift>
          endpoint="/superadmin/gifts"
          params={filtrlar}
          filename={t('nav.saasGifts')}
          ustunlar={[
            { key: 'tenantName', label: t('nav.tenants') },
            { key: 'from', label: t('premiumPage.from'), get: (r) => r.from.fullName },
            { key: 'to', label: t('premiumPage.to'), get: (r) => r.to.fullName },
            { key: 'months', label: t('parentDetail.monthsGive') },
            { key: 'createdAt', label: t('fields.date') },
          ]}
          sanaMaydoni={(r) => r.createdAt}
        />
      </TableToolbar>

      <SectionCard title={t('common.list')} icon={IconGift} color="grape" p={0} pos="relative">
        {gifts.isLoading ? (
          <TableSkeleton rows={6} cols={5} />
        ) : !gifts.items.length ? (
          <EmptyState
            icon={IconGift}
            title={filterActive ? t('common.noMatch') : t('saasGiftsPage.emptyTitle')}
            description={filterActive ? undefined : t('saasGiftsPage.emptyDesc')}
          />
        ) : (
          <Table.ScrollContainer minWidth={760}>
            <FetchOverlay visible={gifts.fetching} />
            <Table highlightOnHover verticalSpacing="sm" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('saasGiftsPage.school')}</Table.Th>
                  <Table.Th>{t('saasGiftsPage.from')}</Table.Th>
                  <Table.Th>{t('saasGiftsPage.to')}</Table.Th>
                  <Table.Th>{t('saasGiftsPage.duration')}</Table.Th>
                  <Table.Th>{t('fields.date')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {gifts.items.map((g) => (
                  <Table.Tr key={g.id}>
                    <Table.Td><Badge variant="light" color="grape">{g.tenantName}</Badge></Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>{g.from.fullName}{g.anonymous && <Badge ml={6} size="xs" color="gray" variant="light">{t('saasGiftsPage.anonymous')}</Badge>}</Text>
                      <Text size="xs" c="dimmed">{formatPhone(g.from.phone)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>{g.to.fullName}</Text>
                      <Text size="xs" c="dimmed">{formatPhone(g.to.phone)}</Text>
                    </Table.Td>
                    <Table.Td><Badge color="yellow" variant="light">{t('saasGiftsPage.months', { n: g.months })}</Badge></Table.Td>
                    <Table.Td>{formatDate(g.createdAt)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>
      <TablePagination
        page={gifts.page}
        pages={gifts.pages}
        total={gifts.total}
        limit={gifts.limit}
        onPage={gifts.setPage}
        onLimit={gifts.setLimit}
      />
    </>
  );
}
